import type { RouteData } from '@cya-routes/shared'

/**
 * Calculate the distance along a route to a specific coordinate
 */
export function getDistanceAlongRoute(
  route: RouteData | null | undefined,
  targetCoords: { lng: number; lat: number }
): number | null {
  if (!route?.coordinates || route.coordinates.length === 0) {
    return null
  }

  let totalDistance = 0
  let closestDistance = 0
  let minDistanceToRoute = Infinity

  // Iterate through route segments
  for (let i = 0; i < route.coordinates.length - 1; i++) {
    const segmentStart = route.coordinates[i]
    const segmentEnd = route.coordinates[i + 1]
    
    // Calculate distance of this segment
    const segmentDistance = calculateDistance(
      segmentStart[1], segmentStart[0],
      segmentEnd[1], segmentEnd[0]
    )

    // Find closest point on this segment to target
    const projection = projectPointToSegment(
      targetCoords,
      { lng: segmentStart[0], lat: segmentStart[1] },
      { lng: segmentEnd[0], lat: segmentEnd[1] }
    )

    const distanceToProjection = calculateDistance(
      targetCoords.lat, targetCoords.lng,
      projection.lat, projection.lng
    )

    if (distanceToProjection < minDistanceToRoute) {
      minDistanceToRoute = distanceToProjection
      closestDistance = totalDistance + projection.t * segmentDistance
    }

    totalDistance += segmentDistance
  }

  // Only return distance if point is reasonably close to route (within 500m)
  // This allows for GPS inaccuracy and POIs placed slightly off the route
  if (minDistanceToRoute > 0.5) {
    return null
  }

  return closestDistance
}

/**
 * Calculate distance between two points in kilometers
 */
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371 // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  return R * c
}

/**
 * Project a point onto a line segment
 */
function projectPointToSegment(
  point: { lng: number; lat: number },
  segmentStart: { lng: number; lat: number },
  segmentEnd: { lng: number; lat: number }
): { lng: number; lat: number; t: number } {
  const dx = segmentEnd.lng - segmentStart.lng
  const dy = segmentEnd.lat - segmentStart.lat
  
  if (dx === 0 && dy === 0) {
    return { ...segmentStart, t: 0 }
  }
  
  const t = Math.max(0, Math.min(1, 
    ((point.lng - segmentStart.lng) * dx + (point.lat - segmentStart.lat) * dy) / 
    (dx * dx + dy * dy)
  ))
  
  return {
    lng: segmentStart.lng + t * dx,
    lat: segmentStart.lat + t * dy,
    t
  }
}